import React from 'react';
import { Alert, Button, Card, Flex, List, Tag, Typography } from 'antd';
import { CheckCircleFilled, StopOutlined, UserDeleteOutlined, WarningFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DeliveryAlerts } from '../../../domain/entities/DeliveryDashboard';

interface DeliveryAlertsPanelProps {
  alerts?: DeliveryAlerts;
  onShowDelayed: () => void;
  onShowTarget: (channelUuid: string, telegramAccountUuid: string) => void;
  onShowAccount: (telegramAccountUuid: string) => void;
}

const shortLink = (link: string) => link.replace(/^https:\/\/t\.me\//, '');

const DeliveryAlertsPanel: React.FC<DeliveryAlertsPanelProps> = ({ alerts, onShowDelayed, onShowTarget, onShowAccount }) => {
  const { t } = useTranslation();
  if (!alerts) return null;

  const hasProblems = alerts.delayed > 0 || alerts.blockedTargets.length > 0 || alerts.failingAccounts.length > 0;
  const errorLabel = (code: string) => t(`delivery-error-${code}`, { ns: 'labels', defaultValue: code });

  if (!hasProblems) {
    return <Alert type="success" showIcon icon={<CheckCircleFilled />} message={t('delivery-alerts-none', { ns: 'labels' })} />;
  }

  return (
    <Card size="small" title={t('delivery-alerts-title', { ns: 'titles' })} className="delivery-alerts">
      <Flex vertical gap={8}>
        {alerts.delayed > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningFilled />}
            message={t('delivery-alerts-delayed', { ns: 'labels', count: alerts.delayed })}
            description={t('delivery-alerts-delayed-hint', { ns: 'labels' })}
            action={<Button size="small" onClick={onShowDelayed}>{t('show', { ns: 'buttons' })}</Button>}
          />
        )}
        {alerts.blockedTargets.length > 0 && (
          <List
            size="small"
            header={<Typography.Text strong><StopOutlined /> {t('delivery-alerts-blocked', { ns: 'labels' })}</Typography.Text>}
            dataSource={alerts.blockedTargets}
            rowKey={(target) => `${target.channelUuid}:${target.telegramAccountUuid}`}
            renderItem={(target) => (
              <List.Item actions={[<Button key="show" size="small" type="link" onClick={() => onShowTarget(target.channelUuid, target.telegramAccountUuid)}>{t('show', { ns: 'buttons' })}</Button>]}>
                <Flex vertical gap={2}>
                  <Typography.Text>
                    {shortLink(target.channelLink) || target.channelUuid} · {target.telegramAccountPhone || target.telegramAccountUuid}
                  </Typography.Text>
                  <Flex gap={6} wrap="wrap" align="center">
                    <Tag color="error">{errorLabel(target.errorCode)}</Tag>
                    {target.upcomingDeliveries > 0 && (
                      <Typography.Text type="danger">{t('delivery-alerts-upcoming', { ns: 'labels', count: target.upcomingDeliveries })}</Typography.Text>
                    )}
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
        )}
        {alerts.failingAccounts.length > 0 && (
          <List
            size="small"
            header={<Typography.Text strong><UserDeleteOutlined /> {t('delivery-alerts-failing-accounts', { ns: 'labels' })}</Typography.Text>}
            dataSource={alerts.failingAccounts}
            rowKey={(account) => account.telegramAccountUuid}
            renderItem={(account) => (
              <List.Item actions={[<Button key="show" size="small" type="link" onClick={() => onShowAccount(account.telegramAccountUuid)}>{t('show', { ns: 'buttons' })}</Button>]}>
                <Flex gap={6} wrap="wrap" align="center">
                  <Typography.Text>{account.telegramAccountPhone || account.telegramAccountUuid}</Typography.Text>
                  <Tag color="error">{errorLabel(account.lastErrorCode)}</Tag>
                </Flex>
              </List.Item>
            )}
          />
        )}
      </Flex>
    </Card>
  );
};

export default DeliveryAlertsPanel;
